<template>
  <div v-redirect-if-no-permission:MANAGE_FINANCE_JOURNALS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-dialog v-model="reverseDialog" max-width="640px" persistent>
      <v-card>
        <v-card-title>
          {{ $t("finance.journals.reverseEntryTitle") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeReverseDialog"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="mb-3" v-if="reversalForm.target_reference_no">
            <strong>{{ reversalForm.target_reference_no }}</strong>
          </div>
          <v-layout wrap>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="reversalForm.reference_no" :label="$t('finance.journals.referenceNo')"></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="reversalForm.journal_date" :label="$t('finance.journals.journalDate')" type="date"></v-text-field>
            </v-flex>
            <v-flex xs12 class="pa-2">
              <v-text-field v-model="reversalForm.reason" :label="$t('finance.common.reason')" :hint="$t('finance.journals.reverseReasonHint')" persistent-hint></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeReverseDialog">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="error" :loading="reverseSaving" @click="submitReversal">{{ $t("finance.journals.reverseEntryConfirm") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card class="mb-2">
      <div class="fin-summary">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.assets.capitalTotal") }}</span>
          <span class="fin-stat-value">{{ formatAmount(capitalTotal) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.assets.vehicleTotal") }}</span>
          <span class="fin-stat-value">{{ formatAmount(vehicleTotal) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.assets.otherAssetsTotal") }}</span>
          <span class="fin-stat-value">{{ formatAmount(otherAssetsTotal) }}</span>
        </div>
      </div>
    </v-card>

    <v-card class="mb-2">
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.assets.registerAsset") }}</span>
        </div>
        <div class="fin-filters">
          <v-select
            class="fin-field--wide"
            :items="entryTypeOptions"
            item-text="text"
            item-value="value"
            v-model="form.entry_kind"
            :label="$t('finance.assets.entryType')"
            hide-details
          ></v-select>
          <v-autocomplete
            class="fin-field--wide"
            :items="assetAccountOptions"
            item-text="text"
            item-value="value"
            v-model="form.asset_account_id"
            :label="$t('finance.assets.assetAccount')"
            hide-details
          ></v-autocomplete>
          <v-autocomplete
            class="fin-field--wide"
            :items="creditAccountOptions"
            item-text="text"
            item-value="value"
            v-model="form.credit_account_id"
            :label="$t('finance.assets.fundingAccount')"
            hide-details
          ></v-autocomplete>
          <v-text-field class="fin-field--wide" v-model="form.asset_name" :label="$t('finance.assets.assetName')" hide-details></v-text-field>
          <v-text-field class="fin-field--wide" v-model="form.asset_category" :label="$t('finance.assets.assetCategory')" hide-details></v-text-field>
          <v-text-field class="fin-field--wide" v-model="form.vendor_name" :label="$t('finance.assets.vendorName')" hide-details></v-text-field>
          <v-text-field class="fin-field" v-model.number="form.amount" type="number" :label="$t('finance.common.amount')" hide-details></v-text-field>
          <v-text-field class="fin-field" v-model="form.journal_date" type="date" :label="$t('finance.assets.postDate')" hide-details></v-text-field>
          <v-text-field class="fin-field" v-model="form.reference_no" :label="$t('finance.journals.referenceNo')" hide-details></v-text-field>
          <v-text-field class="fin-field" v-model="form.currency" :label="$t('finance.common.code')" hide-details></v-text-field>
          <v-text-field class="fin-field--wide" v-model="form.memo" :label="$t('finance.common.memo')" hide-details></v-text-field>
          <v-btn small color="primary" :loading="saving" @click="submitAssetEntry">{{ $t("finance.assets.postEntry") }}</v-btn>
        </div>
      </div>
    </v-card>

    <v-card class="mb-2">
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.assets.filters") }}</span>
        </div>
        <div class="fin-filters">
          <v-text-field class="fin-field" v-model="filters.from" type="date" :label="$t('finance.common.from')" hide-details></v-text-field>
          <v-text-field class="fin-field" v-model="filters.to" type="date" :label="$t('finance.common.to')" hide-details></v-text-field>
          <v-select
            class="fin-field"
            :items="entryTypeOptions"
            item-text="text"
            item-value="value"
            v-model="filters.entry_kind"
            :label="$t('finance.assets.entryType')"
            clearable
            hide-details
          ></v-select>
          <v-autocomplete
            class="fin-field--wide"
            :items="assetFilterOptions"
            item-text="text"
            item-value="value"
            v-model="filters.asset_account_id"
            :label="$t('finance.assets.assetAccount')"
            clearable
            hide-details
          ></v-autocomplete>
          <v-btn small color="primary" @click="loadEntries">{{ $t("finance.common.apply") }}</v-btn>
          <v-btn small flat @click="resetFilters">{{ $t("finance.assets.clearFilters") }}</v-btn>
        </div>
      </div>
    </v-card>

    <v-card>
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.assets.recentEntries") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadEntries">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.refresh") }}</span>
          </v-tooltip>
        </div>
      </div>
      <v-data-table class="fin-dt" :headers="headers" :items="entries" :rows-per-page-items="[10, 25, 50, 100]">
        <template v-slot:items="props">
          <tr>
            <td>{{ formatDate(props.item.journal_date) }}</td>
            <td>{{ formatEntryType(props.item.entry_kind) }}</td>
            <td>{{ props.item.asset_name || '-' }}</td>
            <td>{{ props.item.asset_account ? accountOptionLabel(props.item.asset_account) : '-' }}</td>
            <td>{{ props.item.credit_account ? accountOptionLabel(props.item.credit_account) : '-' }}</td>
            <td>{{ props.item.vendor_name || '-' }}</td>
            <td class="fin-num">{{ formatAmount(props.item.amount) }}</td>
            <td>{{ props.item.memo || '-' }}</td>
            <td>
              <v-chip small :color="props.item.is_reversed ? 'error' : props.item.is_reversal ? 'info' : 'success'" text-color="white">
                {{ formatEntryStatus(props.item) }}
              </v-chip>
            </td>
            <td>
              <div class="entry-actions-cell">
                <span v-if="props.item.is_reversed && props.item.reversed_by_entry" class="caption error--text">
                  {{ $t("finance.journals.reversedBy") }} {{ props.item.reversed_by_entry.reference_no || ('JE-' + props.item.reversed_by_entry.id) }}
                </span>
                <span v-else-if="props.item.is_reversal && props.item.reverse_of_entry" class="caption info--text">
                  {{ $t("finance.journals.reversalOf") }} {{ props.item.reverse_of_entry.reference_no || ('JE-' + props.item.reverse_of_entry.id) }}
                </span>
                <v-btn v-else-if="props.item.can_reverse && hasPermission('REVERSE_FINANCE_JOURNALS')" small color="error" outline @click.stop="openReverseDialog(props.item)">
                  {{ $t("finance.journals.reverseEntry") }}
                </v-btn>
                <span v-else>-</span>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { formatFinanceDate } from "@/mixins/financeDate";
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";

export default {
  name: "AssetEntries",
  title() {
    return this.$t("finance.assets.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent },
  data() {
    return {
      loading: false,
      saving: false,
      reverseSaving: false,
      reverseDialog: false,
      accounts: [],
      entries: [],
      filters: {
        from: null,
        to: null,
        asset_account_id: null,
        entry_kind: null,
      },
      form: this.emptyForm(),
      reversalForm: this.emptyReversalForm(),
    };
  },
  computed: {
    entryTypeOptions() {
      return [
        { text: this.$t("finance.assets.capitalInjection"), value: "capital_injection" },
        { text: this.$t("finance.assets.vehiclePurchase"), value: "vehicle_purchase" },
        { text: this.$t("finance.assets.assetPurchase"), value: "asset_purchase" },
      ];
    },
    assetAccountOptions() {
      return this.accounts
        .filter(function (account) {
          return account.type === "ASSET" && this.isPostableLeaf(account);
        }, this)
        .map(function (account) {
          return {
            text: this.accountOptionLabel(account),
            value: account.id,
          };
        }, this);
    },
    // History filter: unrestricted — old entries may sit on accounts that are
    // no longer valid posting targets (now-parents, deactivated).
    assetFilterOptions() {
      return this.accounts
        .filter(function (account) {
          return account.type === "ASSET";
        })
        .map(function (account) {
          return {
            text: this.accountOptionLabel(account),
            value: account.id,
          };
        }, this);
    },
    creditAccountOptions() {
      return this.accounts
        .filter(function (account) {
          return this.isPostableLeaf(account);
        }, this)
        .map(function (account) {
          return {
            text: this.accountOptionLabel(account),
            value: account.id,
          };
        }, this);
    },
    headers() {
      return [
        { text: this.$t("finance.common.date"), value: "journal_date" },
        { text: this.$t("finance.assets.entryType"), value: "entry_kind" },
        { text: this.$t("finance.assets.assetName"), value: "asset_name" },
        { text: this.$t("finance.assets.assetAccount"), value: "asset_account" },
        { text: this.$t("finance.assets.fundingAccount"), value: "credit_account" },
        { text: this.$t("finance.assets.vendorName"), value: "vendor_name" },
        { text: this.$t("finance.common.amount"), value: "amount", align: "end" },
        { text: this.$t("finance.common.memo"), value: "memo" },
        { text: this.$t("finance.assets.status"), value: "status", sortable: false },
        { text: this.$t("finance.common.actions"), value: "actions", sortable: false },
      ];
    },
    capitalTotal() {
      return this.sumEntriesByKind("capital_injection");
    },
    vehicleTotal() {
      return this.sumEntriesByKind("vehicle_purchase");
    },
    otherAssetsTotal() {
      return this.sumEntriesByKind("asset_purchase");
    },
  },
  mounted() {
    this.loadAccounts();
    this.loadEntries();
  },
  methods: {
    // Policy doc 2026-08-23 item 1: pickers offer only active LEAF accounts —
    // parents were the silent-slippage trap (first substring match for a
    // nonexistent typed code). Mirrors the server-side guard.
    isPostableLeaf(account) {
      return (
        account.allows_manual_posting !== false &&
        account.is_active !== false &&
        account.has_children !== true &&
        account.kind !== "closing"
      );
    },
    emptyForm() {
      return {
        asset_account_id: null,
        credit_account_id: null,
        entry_kind: "capital_injection",
        asset_name: null,
        asset_category: null,
        vendor_name: null,
        amount: null,
        journal_date: null,
        reference_no: null,
        currency: "SYP",
        memo: null,
      };
    },
    emptyReversalForm() {
      return {
        id: null,
        target_reference_no: null,
        reference_no: null,
        journal_date: null,
        reason: null,
      };
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    localizedAccountName(account) {
      if (!account) {
        return "-";
      }

      if (this.$i18n && this.$i18n.locale === "ar" && account.name_ar) {
        return account.name_ar;
      }

      return account.name || account.name_ar || "-";
    },
    accountOptionLabel(account) {
      return account.code + " - " + this.localizedAccountName(account);
    },
    loadAccounts() {
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_CHART_OF_ACCOUNTS)
        .then(
          function (response) {
            this.accounts = response.data.data || [];
          }.bind(this)
        )
        .catch(function () {});
    },
    loadEntries() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_ASSETS, {
          params: {
            from: this.filters.from,
            to: this.filters.to,
            asset_account_id: this.filters.asset_account_id,
            entry_kind: this.filters.entry_kind,
            perPage: 100,
          },
        })
        .then(
          function (response) {
            this.entries = response.data.data.data || [];
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.assets.failedLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    submitAssetEntry() {
      if (!this.form.asset_account_id || !this.form.credit_account_id || !this.form.amount || !this.form.asset_name) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.assets.requiredFields"), "red");
        return;
      }

      this.saving = true;
      var payload = Object.assign({}, this.form, {
        amount: this.toStoredAmount(this.form.amount),
      });
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_ASSETS, payload)
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.assets.assetPosted"), "green");
            this.form = this.emptyForm();
            this.loadEntries();
            this.loadAccounts();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.assets.failedPost")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.saving = false;
          }.bind(this)
        );
    },
    openReverseDialog(entry) {
      this.reversalForm = {
        id: entry.id,
        target_reference_no: entry.reference_no || ("JE-" + entry.id),
        reference_no: null,
        journal_date: null,
        reason: null,
      };
      this.reverseDialog = true;
    },
    closeReverseDialog() {
      this.reverseDialog = false;
      this.reversalForm = this.emptyReversalForm();
    },
    submitReversal() {
      if (!this.reversalForm.id) {
        return;
      }

      this.reverseSaving = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_REVERSE_JOURNAL_ENTRY(this.reversalForm.id), {
          reference_no: this.reversalForm.reference_no,
          journal_date: this.reversalForm.journal_date,
          reason: this.reversalForm.reason,
        })
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.journals.reversePosted"), "green");
            this.closeReverseDialog();
            this.loadEntries();
            this.loadAccounts();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.journals.failedReverse")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.reverseSaving = false;
          }.bind(this)
        );
    },
    resetFilters() {
      this.filters = {
        from: null,
        to: null,
        asset_account_id: null,
        entry_kind: null,
      };
      this.loadEntries();
    },
    sumEntriesByKind(kind) {
      return this.entries
        .filter(function (entry) {
          return entry.entry_kind === kind;
        })
        .reduce(function (sum, entry) {
          return sum + Number(entry.amount || 0);
        }, 0);
    },
    formatEntryType(value) {
      if (!value) {
        return "-";
      }

      var map = {
        capital_injection: this.$t("finance.assets.capitalInjection"),
        vehicle_purchase: this.$t("finance.assets.vehiclePurchase"),
        asset_purchase: this.$t("finance.assets.assetPurchase"),
      };

      return map[value] || value;
    },
    formatEntryStatus(entry) {
      if (entry.is_reversed) {
        return this.$t("finance.journals.statusReversed");
      }

      if (entry.is_reversal) {
        return this.$t("finance.journals.statusReversal");
      }

      return this.$t("finance.journals.statusPosted");
    },
    formatAmount(value) {
      return this.formatMoney(value);
    },
    formatDate(value) {
      // One shared finance formatter (accountant doc 2026-10-03 item 1.8).
      return formatFinanceDate(value);
    },
  },
};
</script>

<style scoped>
.entry-actions-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
</style>